import type {
  DeviceLabel,
  DevicesSettings,
  NgavSettings,
  WifiPhishingSettings,
} from "@/_store/devices-settings.module.ts";
import type { EmailSecuritySettings } from "@/_store/email-security/email-settings.module.ts";
import type {
  EndpointSecurityGlobalPostureSettings,
  GlobalPolicyApiResponse,
  GlobalPolicyConfig,
  GlobalPolicyEmailSecurityApiResponse,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { GlobalPolicySection } from "@/constants/msp.ts";
import { removeKeyDeepClone } from "@coronet/cui";
import { stripIdsFromMalwareScansSchedule } from "@/_helpers/global-policy.ts";
import { useMspDevicesStore } from "@/_store/msp/devices/msp-devices.module.ts";
import { storeToRefs } from "pinia";
import { PredefinedDeviceLabelNameMap } from "@/constants/devices.ts";

/**
 * Shared utility that populates empty label arrays with the "ALL_DEVICES" label
 * whenever the corresponding "enabled" flag is true.
 *
 * @template T - Type of the settings object.
 * @param settings - The original settings object to normalize.
 * @param fields - Array of objects describing which boolean flag enables which label array.
 * @returns A new settings object, with empty label arrays filled appropriately.
 */
async function normalizeLabels<T extends object>(
  settings: T,
  fields: Array<{ enabledKey: keyof T & string; labelKey: keyof T & string }>
): Promise<T> {
  const mspDevicesStore = useMspDevicesStore();
  const { availableLabels } = storeToRefs(mspDevicesStore);
  if (!availableLabels.value.length) {
    await mspDevicesStore.getLabels();
  }
  const result: T = { ...settings };
  for (const { enabledKey, labelKey } of fields) {
    const enabledFlag = result[enabledKey] as boolean;
    const labelsArray = result[labelKey] as DeviceLabel[];
    if (enabledFlag && Array.isArray(labelsArray) && labelsArray.length === 0) {
      result[labelKey] = (availableLabels.value ?? []).filter(
        (label) => label.name === PredefinedDeviceLabelNameMap.ALL_DEVICES
      ) as T[typeof labelKey];
    }
  }
  return result;
}

// For NGAV settings: map each boolean "enabled" property to its corresponding labels array.
const NGAV_FIELDS: Array<{ enabledKey: keyof NgavSettings; labelKey: keyof NgavSettings }> = [
  { enabledKey: "atcEnabled", labelKey: "atcLabels" },
  { enabledKey: "vssPolicyEnabled", labelKey: "vssPolicyLabels" },
  { enabledKey: "sharedOnAccessCooperationEnabled", labelKey: "sharedOnAccessCooperationLabels" },
  { enabledKey: "initialDevicesScanEnabled", labelKey: "initialDevicesScanLabels" },
  { enabledKey: "enforceContainerQuarantineEnabled", labelKey: "enforceContainerQuarantineLabels" },
];

// For Wifi Phishing settings: map the boolean "wifiPhishingEnabled" to its labels array.
const WIFI_PHISHING_FIELDS: Array<{
  enabledKey: keyof WifiPhishingSettings;
  labelKey: keyof WifiPhishingSettings;
}> = [{ enabledKey: "wifiPhishingEnabled", labelKey: "wifiPhishingLabels" }];

/**
 * Ensures that for each NGAV feature flagged as enabled, its labels array is populated
 * with the "ALL_DEVICES" label if the array is currently empty.
 *
 * @param settings - The NGAV settings object from the store.
 * @returns A promise that resolves to the normalized NGAV settings.
 */
export async function normalizeEndpointSecurityNgavSettings(
  settings: NgavSettings
): Promise<NgavSettings> {
  if (!settings.realTimeMalwareProtectionEnabled) {
    return settings;
  }
  return normalizeLabels(settings, NGAV_FIELDS);
}

/**
 * Ensures that if WiFi Phishing is enabled, the wifiPhishingLabels array is populated
 * with the "ALL_DEVICES" label if it is currently empty.
 *
 * @param settings - The WifiPhishingSettings object from the store.
 * @returns A promise that resolves to the normalized WifiPhishingSettings.
 */
export async function normalizeEndpointSecurityAddonsSettings(
  settings: WifiPhishingSettings
): Promise<WifiPhishingSettings> {
  return normalizeLabels(settings, WIFI_PHISHING_FIELDS);
}

const DEVICES_SETTINGS_FIELDS: Array<{
  enabledKey: keyof DevicesSettings;
  labelKey: keyof DevicesSettings;
}> = [
  { enabledKey: "allowToUninstallEnabled", labelKey: "allowToUninstallLabels" },
  { enabledKey: "visibilityModeEnabled", labelKey: "visibilityModeLabels" },
];

/**
 * Ensures that for each Devices feature flagged as enabled, its labels array is populated
 * with the "ALL_DEVICES" label if the array is currently empty.
 *
 * @param settings - The DevicesSettings object from the store.
 * @returns A promise that resolves to the normalized DevicesSettings.
 */
export async function normalizeDevicesSettings(
  settings: DevicesSettings
): Promise<DevicesSettings> {
  return normalizeLabels(settings, DEVICES_SETTINGS_FIELDS);
}

/**
 * Strips `temporaryId` fields (used for UI tracking) from posture settings before PUT,
 * and removes internal IDs from malware scan schedule items.
 *
 * @param settings - The device posture settings object from the store.
 * @returns A cleaned posture settings object safe to send to the API (IDs stripped).
 */
export function normalizePostureSettings(settings: EndpointSecurityGlobalPostureSettings) {
  return removeKeyDeepClone(
    {
      ...settings,
      malwareScansScheduleSettings: stripIdsFromMalwareScansSchedule(
        settings.malwareScansScheduleSettings
      ),
    },
    "temporaryId"
  );
}

/**
 * Transforms the GET response from the global policy API into the flat `EmailSecuritySettings`
 * shape used by the frontend. The API nests `adminEmail`, `blockedEmailReportsConfig`, and
 * `autoForwardingBlockingPolicy` inside `mspEmailSecurityForwardingReportingSettingsDto`.
 * Workspace-specific selection fields absent from the API response are filled with defaults.
 *
 * @param settings - The email security settings as returned by the global policy API.
 * @returns A full `EmailSecuritySettings` object ready for use in the store.
 */
function denormalizeEmailSecuritySettings(
  settings: GlobalPolicyEmailSecurityApiResponse
): EmailSecuritySettings {
  const { mspEmailSecurityForwardingReportingSettingsDto, ...rest } = settings;
  const { adminEmail, blockedEmailReportsConfig, autoForwardingBlockingPolicy } =
    mspEmailSecurityForwardingReportingSettingsDto;
  return {
    ...rest,
    adminEmail,
    blockedEmailReportsConfig,
    autoForwardingBlockingPolicy,
  };
}

/**
 * Converts the full global policy API response into the shape expected by the frontend.
 * Each section with an API-specific format is denormalized here.
 *
 * @param response - The raw API response from the global policy endpoint.
 * @returns A `GlobalPolicyConfig` with all sections in the frontend-ready format.
 */
export function denormalizeGlobalPolicyResponse(
  response: GlobalPolicyApiResponse
): GlobalPolicyConfig {
  const { [GlobalPolicySection.EMAIL_SECURITY]: emailSecurity, ...rest } = response;
  return {
    ...rest,
    ...(emailSecurity && {
      [GlobalPolicySection.EMAIL_SECURITY]: denormalizeEmailSecuritySettings(emailSecurity),
    }),
  };
}

/**
 * Strips workspace-specific fields from email security settings before PUT to the global policy API.
 * The global policy API only accepts `enabled`/`frequency` for blocked email reports
 * and `enabled` for auto-forwarding blocking — user selection fields are workspace-only.
 *
 * @param settings - The full email security settings object from the store.
 * @returns A settings object with only the API-relevant fields in the two config sections.
 */
export function normalizeEmailSecuritySettings(settings: EmailSecuritySettings) {
  const { blockedEmailReportsConfig, autoForwardingBlockingPolicy, adminEmail, ...rest } = settings;
  return {
    ...rest,
    mspEmailSecurityForwardingReportingSettingsDto: {
      adminEmail,
      blockedEmailReportsConfig: {
        enabled: blockedEmailReportsConfig.enabled,
        frequency: blockedEmailReportsConfig.frequency,
      },
      autoForwardingBlockingPolicy: {
        enabled: autoForwardingBlockingPolicy.enabled,
      },
    },
  };
}
